import { lazy, Suspense } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "./contexts/AuthContext";
import { ErrorBoundary } from "./components/error";
import { Layout } from "./components/layout/Layout";
import { ProtectedAdminRoute } from "./components/auth/ProtectedAdminRoute";
import ScrollToTop from "./components/ScrollToTop";
import TrailingSlashRedirect from "./components/TrailingSlashRedirect";

const CookieConsent = lazy(() => import("./components/CookieConsent").then(m => ({ default: m.CookieConsent })));
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));

const HomePage = lazy(() => import("./pages/HomePage"));
const CheckoutSuccessPage = lazy(() => import("./pages/CheckoutSuccessPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const SignupPage = lazy(() => import("./pages/SignupPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const FeedbackPage = lazy(() => import("./pages/FeedbackPage"));
const ThemePreviewPage = lazy(() => import("./pages/ThemePreviewPage"));
const LearningsPage = lazy(() => import("./pages/LearningsPage"));
const BooksPage = lazy(() => import("./pages/BooksPage"));
const BookDetailPage = lazy(() => import("./pages/BookDetailPage"));
const PracticePage = lazy(() => import("./pages/PracticePage"));
const MyQuizzesPage = lazy(() => import("./pages/MyQuizzesPage"));
const Impressum = lazy(() => import("./pages/Impressum"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Barrierefreiheit = lazy(() => import("./pages/Barrierefreiheit"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <ErrorBoundary>
        <AuthProvider>
          <TooltipProvider>
            <Suspense fallback={null}><Toaster /></Suspense>
            <Suspense fallback={null}><Sonner /></Suspense>
            <BrowserRouter>
            <ScrollToTop />
              <TrailingSlashRedirect />
              <Suspense fallback={
                <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-background/95">
                  <div className="flex flex-col items-center gap-2">
                    <div className="h-10 w-10 rounded-full border-4 border-t-primary animate-spin" />
                    <div className="text-lg text-muted-foreground">Loading...</div>
                  </div>
                </div>
              }>
              <Routes>
                <Route path="/" element={<Layout />}>
                  <Route index element={<HomePage />} />
                  <Route path="login" element={<LoginPage />} />
                  <Route path="signup" element={<SignupPage />} />
                  <Route path="pricing" element={<PricingPage />} />
                  <Route path="checkout/success" element={<CheckoutSuccessPage />} />
                  <Route path="theme-preview" element={<ThemePreviewPage />} />

                  <Route path="/legal" element={<Impressum />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/wcag" element={<Barrierefreiheit />} />
                  <Route path="/tos" element={<TermsOfService />} />
                  <Route path="blog" element={<BlogPage />} />
                  <Route path="blog/:slug" element={<BlogPostPage />} />
                </Route>
                
                <Route path="/" element={<Layout requireAuth />}>
                  <Route path="dashboard" element={<DashboardPage />} />
                  {/* <Route path="profile" element={<ProfilePage />} /> */}
                  <Route path="feedback" element={<FeedbackPage />} />
                  <Route path="learnings" element={<LearningsPage />} />
                  <Route path="practice" element={<PracticePage />} />
                  <Route path="my-quizzes" element={<MyQuizzesPage />} />
                  <Route path="books" element={<BooksPage />} />
                  <Route path="book/:id" element={<BookDetailPage />} />
                  <Route path="admin"
                    element={
                      <ProtectedAdminRoute>
                        <AdminPage />
                      </ProtectedAdminRoute>
                    } 
                  />
                </Route>

                <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
              <Suspense fallback={null}><CookieConsent /></Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </AuthProvider>
      </ErrorBoundary>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
